import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { toast } from "sonner";
import { Mail, Lock, ShieldCheck } from "lucide-react";
import CavilowLogo from "../components/CavilowLogo";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title="Welcome back" subtitle="Sign in to continue your job search.">
      <form onSubmit={submit} className="space-y-4">
        <Field icon={Mail} label="Email">
          <input
            data-testid="login-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full bg-transparent outline-none text-sm text-white placeholder:text-slate-500"
            placeholder="you@work.com"
          />
        </Field>
        <Field icon={Lock} label="Password">
          <input
            data-testid="login-password"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full bg-transparent outline-none text-sm text-white placeholder:text-slate-500"
            placeholder="••••••••"
          />
        </Field>
        <div className="flex justify-end -mt-1">
          <Link
            to="/forgot-password"
            data-testid="login-forgot-link"
            className="text-xs font-semibold text-[var(--cavilo-blue)] hover:underline"
          >
            Forgot password?
          </Link>
        </div>
        <button
          data-testid="login-submit"
          type="submit"
          disabled={busy}
          className="w-full btn-neon py-3 text-sm disabled:opacity-60 btn-press"
        >
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>

      <p className="text-sm text-slate-400 text-center mt-6">
        Don&apos;t have an account?{" "}
        <Link to="/signup" data-testid="login-to-signup" className="font-semibold text-[#80FFEA] hover:text-white">
          Sign up
        </Link>
      </p>
    </AuthShell>
  );
}

export function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen grid md:grid-cols-2" style={{ background: "var(--cavilo-bg)" }}>
      <div className="hidden md:flex flex-col justify-between p-12 relative overflow-hidden" style={{ background: "linear-gradient(160deg, rgba(157,0,255,0.15) 0%, rgba(0,229,255,0.08) 60%, transparent 100%)" }}>
        <div className="absolute inset-0 dot-bg opacity-25 pointer-events-none" />
        <Link to="/" className="relative">
          <CavilowLogo size={40} textClass="text-2xl text-white" />
        </Link>
        <div className="relative">
          <h2 className="font-display font-black text-4xl tracking-tight text-white">
            Find better jobs.<br />
            <span className="text-neon">Apply smarter.</span>
          </h2>
          <p className="mt-4 text-slate-300 max-w-md leading-relaxed">
            Your AI-powered Job Search Agent. Upload your CV once and let Cavilow guide your job search.
          </p>
          <div className="mt-6 flex items-center gap-2 text-xs font-semibold">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-slate-300">
              <ShieldCheck size={12} className="text-[#80FFEA]" /> Encrypted
            </span>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-slate-300">
              Your data, your control
            </span>
          </div>
          <p className="mt-4 text-[11px] text-slate-500 italic">Career + Vision + Flow</p>
        </div>
        <div className="relative text-xs text-slate-500">© {new Date().getFullYear()} Cavilow</div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm fade-up">
          <Link to="/" className="md:hidden flex items-center gap-2 mb-8">
            <CavilowLogo size={36} textClass="text-2xl text-white" />
          </Link>
          <h1 className="font-display font-black text-3xl tracking-tight text-white">{title}</h1>
          {subtitle && <p className="mt-1 text-slate-400 text-sm">{subtitle}</p>}
          <div className="mt-8">{children}</div>
        </div>
      </div>
    </div>
  );
}

export function Field({ icon: Icon, label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">{label}</span>
      <div className="mt-1.5 flex items-center gap-2 rounded-xl border border-white/10 focus-within:border-[#00E5FF] bg-white/5 px-3.5 py-2.5 transition-colors">
        <Icon size={16} className="text-slate-500" />
        {children}
      </div>
    </label>
  );
}
